export type CleaningType = 'standard' | 'deep' | 'move-out';
export type CleaningFrequency = 'one-time' | 'weekly' | 'every-2-weeks' | 'monthly';
export type CleaningExtra = 'inside-oven' | 'inside-fridge' | 'interior-windows' | 'laundry';

export interface MapleMopEstimateInput {
  bedrooms: number;
  bathrooms: number;
  cleaningType: CleaningType;
  frequency: CleaningFrequency;
  extras: readonly string[];
}

export interface MapleMopEstimateLine {
  id: string;
  label: string;
  detail?: string;
  amount: number;
}

export interface MapleMopEstimate {
  baseCleaningSubtotal: number;
  adjustedCleaningSubtotal: number;
  frequencyDiscount: number;
  extrasSubtotal: number;
  total: number;
  lineItems: MapleMopEstimateLine[];
}

export const MAPLE_MOP_QUOTE_URL = 'https://maple-mop-quote.base44.app';

export const CLEANING_TYPE_LABELS: Record<CleaningType, string> = {
  standard: 'Standard clean',
  deep: 'Deep clean',
  'move-out': 'Move-out clean',
};

export const CLEANING_FREQUENCY_LABELS: Record<CleaningFrequency, string> = {
  'one-time': 'One-time',
  weekly: 'Weekly',
  'every-2-weeks': 'Every 2 weeks',
  monthly: 'Monthly',
};

export const CLEANING_EXTRA_OPTIONS: ReadonlyArray<{
  id: CleaningExtra;
  label: string;
  price: number;
}> = [
  { id: 'inside-oven', label: 'Inside oven', price: 35 },
  { id: 'inside-fridge', label: 'Inside fridge', price: 30 },
  { id: 'interior-windows', label: 'Interior windows', price: 40 },
  { id: 'laundry', label: 'Laundry', price: 25 },
];

const CLEANING_MULTIPLIERS: Record<CleaningType, number> = {
  standard: 1,
  deep: 1.5,
  'move-out': 1.8,
};

const FREQUENCY_DISCOUNTS: Record<CleaningFrequency, number> = {
  'one-time': 0,
  weekly: 0.2,
  'every-2-weeks': 0.15,
  monthly: 0.1,
};

export function roundUsd(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

export function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function calculateMapleMopEstimate(input: MapleMopEstimateInput): MapleMopEstimate {
  const bedrooms = Math.max(0, input.bedrooms);
  const bathrooms = Math.max(0, input.bathrooms);
  const multiplier = CLEANING_MULTIPLIERS[input.cleaningType];
  const discountRate = FREQUENCY_DISCOUNTS[input.frequency];
  const knownExtras = CLEANING_EXTRA_OPTIONS.filter((extra) => input.extras.includes(extra.id));

  const bedroomPrice = roundUsd(bedrooms * 25);
  const bathroomPrice = roundUsd(bathrooms * 30);
  const baseCleaningSubtotal = roundUsd(90 + bedroomPrice + bathroomPrice);
  const adjustedCleaningSubtotal = roundUsd(baseCleaningSubtotal * multiplier);
  const multiplierAdjustment = roundUsd(adjustedCleaningSubtotal - baseCleaningSubtotal);
  const extrasSubtotal = roundUsd(knownExtras.reduce((sum, extra) => sum + extra.price, 0));
  const frequencyDiscount = roundUsd((adjustedCleaningSubtotal + extrasSubtotal) * discountRate);
  const total = roundUsd(adjustedCleaningSubtotal + extrasSubtotal - frequencyDiscount);

  const lineItems: MapleMopEstimateLine[] = [
    { id: 'base', label: 'Base service', detail: 'Starting price', amount: 90 },
    {
      id: 'bedrooms',
      label: 'Bedrooms',
      detail: `${bedrooms} × ${formatUsd(25)}`,
      amount: bedroomPrice,
    },
    {
      id: 'bathrooms',
      label: 'Bathrooms',
      detail: `${bathrooms} × ${formatUsd(30)}`,
      amount: bathroomPrice,
    },
  ];

  if (multiplierAdjustment > 0) {
    lineItems.push({
      id: 'cleaning-type',
      label: `${CLEANING_TYPE_LABELS[input.cleaningType]} adjustment (×${multiplier.toFixed(1)})`,
      detail: `Applied to ${formatUsd(baseCleaningSubtotal)} cleaning subtotal`,
      amount: multiplierAdjustment,
    });
  }

  for (const extra of knownExtras) {
    lineItems.push({
      id: `extra-${extra.id}`,
      label: extra.label,
      detail: 'Extra service',
      amount: extra.price,
    });
  }

  if (discountRate > 0) {
    lineItems.push({
      id: 'frequency-discount',
      label: `${CLEANING_FREQUENCY_LABELS[input.frequency]} discount (−${Math.round(discountRate * 100)}%)`,
      detail: `Applied after extras to ${formatUsd(adjustedCleaningSubtotal + extrasSubtotal)}`,
      amount: -frequencyDiscount,
    });
  } else {
    lineItems.push({
      id: 'frequency-discount',
      label: 'One-time visit',
      detail: 'No recurring frequency discount',
      amount: 0,
    });
  }

  return {
    baseCleaningSubtotal,
    adjustedCleaningSubtotal,
    frequencyDiscount,
    extrasSubtotal,
    total,
    lineItems,
  };
}
